import { apiErrorBodySchema, type ErrorCode } from '@goodway/shared';
import { clearSessionToken, getSessionToken } from './embeddedToken';

/** Ошибка API в едином формате или сетевая ошибка (code = NETWORK_ERROR). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions extends RequestInit {
  /** HTTP-статусы, при которых тело ответа — данные, а не ошибка (например, 503 у /health/ready). */
  acceptStatuses?: number[];
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export async function requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { acceptStatuses = [], headers, ...init } = options;
  const method = (init.method ?? 'GET').toUpperCase();
  const requestHeaders: Record<string, string> = { accept: 'application/json' };
  // Сервер отклоняет изменяющие запросы без этого заголовка (защита от подделки запросов).
  if (!SAFE_METHODS.has(method)) requestHeaders['x-goodway-csrf'] = '1';
  // Content-Type объявляется только вместе с телом: иначе сервер вернёт 400.
  if (init.body !== undefined && init.body !== null) requestHeaders['content-type'] = 'application/json';
  // Встроенный режим Bitrix24: cookie может быть заблокирована, сессия передаётся заголовком.
  const token = getSessionToken();
  if (token) requestHeaders.authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      method,
      headers: { ...requestHeaders, ...(headers as Record<string, string> | undefined) },
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Сервер недоступен. Проверьте подключение и повторите.');
  }

  if (response.status === 401 && token) clearSessionToken();

  const body: unknown = response.status === 204 ? undefined : await response.json().catch(() => undefined);
  if (response.ok || acceptStatuses.includes(response.status)) {
    return body as T;
  }

  const parsed = apiErrorBodySchema.safeParse(body);
  if (parsed.success) {
    const { code, message, requestId } = parsed.data.error;
    throw new ApiError(response.status, code, message, requestId);
  }
  throw new ApiError(
    response.status,
    response.status >= 500 ? 'INTERNAL_ERROR' : 'BAD_REQUEST',
    `Сервер ответил ошибкой ${response.status}`,
    response.headers.get('x-request-id') ?? undefined,
  );
}
